import { type MetaFunction } from "@vercel/remix"
import { useLoaderData } from "@remix-run/react"
import { marked } from "marked"

export const meta: MetaFunction = () => {
  return [
    { title: "GDPR | Best of Me" },
    { name: "description", content: "Privacy Policy for Best of Me - Your Mental Health Companion" },
  ]
}

const privacyPolicyMd = `
# Accessibility Statement

At Best of Me, we are committed to ensuring digital accessibility for all users, including those with disabilities. We strive to continually improve the user experience of our app and website to meet or exceed accessibility standards.

## Our Accessibility Standards

We work to comply with the **Web Content Accessibility Guidelines (WCAG) 2.1** Level AA, the standard that outlines best practices to make web content accessible to individuals with disabilities, including those with visual, hearing, cognitive, and motor impairments.

## Accessibility Features

Our app and website incorporate several features designed to enhance accessibility:

- **Screen Reader Compatibility**: We ensure our content is compatible with screen readers by using proper HTML tags, alt text for images, and meaningful link descriptions.
- **Keyboard Navigation**: Our app and website support full keyboard navigation, enabling users to access all interactive features without a mouse.
- **Color Contrast and Text Readability**: We use a color palette and text sizes that are optimized for readability and meet contrast standards.
- **Consistent Layout**: We maintain a consistent layout and navigation structure to help users locate information easily.
- **Closed Captions and Transcriptions**: Where possible, we provide captions for video content and transcripts for audio content to accommodate users with hearing impairments.

## Accessibility Efforts and Ongoing Improvements

Our team regularly assesses and enhances accessibility features through:

- **User Testing**: We conduct usability testing that includes users with disabilities to gather feedback on accessibility.
- **Third-Party Audits**: We work with accessibility experts to audit our app and website and identify areas for improvement.
- **Continuous Updates**: Accessibility is an ongoing process, and we make continuous updates to meet new standards and enhance user experience.

## Feedback and Support

We welcome feedback on the accessibility of our app and website. If you encounter any accessibility barriers or have suggestions for improvement, please contact us. We are committed to addressing all issues in a timely manner.

**Contact Information**:

- **Email**: [dev@bestofme.app](mailto:dev@bestofme.app)
- **Mailing Address**: [Šalviová 876/30, 821 01 Bratislava, Slovakia, EU]

## Accessibility Compliance Goal

Our goal is to ensure that Best of Me is fully accessible to all users and complies with applicable accessibility regulations and standards, including the Americans with Disabilities Act (ADA) and WCAG 2.1 guidelines.

---

**Ongoing Commitment to Accessibility**

Accessibility is central to our mission at Best of Me. We are dedicated to creating an inclusive digital environment and ensuring all users have equal access to our content, tools, and resources.

Thank you for helping us make Best of Me more accessible to everyone.

**Effective Date: Nov 5, 2024**
`

export async function loader() {
  const htmlContent = marked(privacyPolicyMd)
  return { htmlContent }
}

export default function Privacy() {
  const { htmlContent } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <article 
          className="prose prose-sm max-w-none prose-headings:text-lg prose-p:text-sm prose-li:text-sm"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </div>
    </div>
  )
}
